import { useAuth, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { Gift, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useConsumerReferral } from "../../hooks/useConsumerReferral";
import { useDashboardShellCapabilities } from "../../hooks/useDashboardShellCapabilities";
import { useWaitlist } from "../../hooks/useWaitlist";
import {
  extractReferralBannerAckFromBootstrap,
  fetchAppState,
  putReferralBannerAck,
  shouldShowReferralBanner,
  type ReferralBannerAck,
} from "../../lib/app-state-api";
import type { ReferralIdentity } from "../../lib/dashboard-settings";
import {
  captureReferralSurfaceClicked,
  captureReferralSurfaceDismissed,
  captureReferralSurfaceShown,
  type ReferralCapture,
  type ReferralShareAction,
} from "../../lib/referral-analytics";
import {
  referralSurfaceCopy,
  type ReferralSurfaceCopy,
} from "../../lib/referral-surface-copy";
import { ReferralShareActions } from "./referral-share-actions";
import type { UseConsumerReferralState } from "../../hooks/useConsumerReferral";

/**
 * Presentational referral banner: reward-aware headline + the shared share
 * actions + a dismiss control. Renders from a resolved `copy` — the caller
 * (container) decides whether there is anything to say at all.
 */
export function ReferralBanner({
  copy,
  referral,
  identity,
  onAction,
  onDismiss,
}: {
  copy: ReferralSurfaceCopy;
  referral: UseConsumerReferralState;
  identity: ReferralIdentity;
  onAction?: (action: ReferralShareAction) => void;
  onDismiss: () => void;
}) {
  return (
    <section
      data-testid="referral-banner"
      data-incentive={copy.incentive}
      aria-label="Refer a friend"
      className="surface-panel relative mb-5 bg-card p-4 sm:p-5"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss referral banner"
        data-testid="referral-banner-dismiss"
        className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <span className="rounded-xl bg-primary/10 p-2 text-primary">
          <Gift className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            {copy.headline}
          </h2>
          <p className="text-sm leading-6 text-muted-foreground">{copy.body}</p>
        </div>
      </div>
      <ReferralShareActions
        referral={referral}
        identity={identity}
        onAction={onAction}
        className="mt-4"
      />
    </section>
  );
}

export interface ReferralBannerContainerProps {
  /**
   * The dismissal decoded from the `/api/users/me` bootstrap (DashboardPage
   * already has it). `null` = bootstrap resolved, no row. When OMITTED
   * (shell routes without a bootstrap: Offers / Feed / Threads) the container
   * reads `/api/users/me/app-state` itself and renders nothing until that
   * settles — a banner must never flash and then vanish.
   */
  bootstrapAck?: ReferralBannerAck | null;
  /**
   * Target-area label when the buyer is queued for a pre-launch metro. When
   * OMITTED the container resolves it via `useWaitlist` (marketplace-flag
   * gated, SILL-504); a caller that already owns that read passes it through.
   */
  waitlistArea?: string | null;
}

/**
 * Persistent, dismissible referral banner for the consumer dashboard shell.
 * Shows ONLY when it has something concrete to promise (see
 * `referralSurfaceCopy`): a live admin-enabled incentive, or the waitlist bump
 * when the buyer is actually queued. Dismissal persists per user via
 * `user_app_state` (`consumer.dashboard/referral_banner.v1`, keyed on the
 * incentive version — a NEW enabled incentive re-surfaces it once).
 *
 * Authed-only by construction: it mounts inside the authed dashboard frames
 * (never `PreviewDashboardShellInner`), and the consumer app is
 * consumer-role only, so no pro role and no `/preview/*` fixture path ever
 * renders it.
 */
export function ReferralBannerContainer({
  bootstrapAck,
  waitlistArea: waitlistAreaProp,
}: ReferralBannerContainerProps) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const posthog = usePostHog();
  const referral = useConsumerReferral(getToken);
  const { marketplaceEnabled } = useDashboardShellCapabilities();
  const { waitlist } = useWaitlist({
    enabled: waitlistAreaProp === undefined && marketplaceEnabled,
  });
  const waitlistArea =
    waitlistAreaProp === undefined
      ? (waitlist?.area_label ?? null)
      : waitlistAreaProp;

  // undefined = still resolving (self-fetch path), null = no row.
  const [fetchedAck, setFetchedAck] = useState<ReferralBannerAck | null | undefined>(
    bootstrapAck === undefined ? undefined : null,
  );
  const [dismissedNow, setDismissedNow] = useState(false);
  // Clerk's `getToken` is a new identity every render; hold it in a ref so
  // the one-shot app-state read below never re-runs (the useWaitlist idiom).
  const tokenRef = useRef(getToken);
  useEffect(() => {
    tokenRef.current = getToken;
  }, [getToken]);
  const selfFetchAck = bootstrapAck === undefined;

  useEffect(() => {
    if (!selfFetchAck) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await tokenRef.current();
        const state = token ? await fetchAppState(token) : null;
        if (cancelled) return;
        setFetchedAck(
          extractReferralBannerAckFromBootstrap(state?.app_state ?? null),
        );
      } catch {
        // A failed read must not hide the banner forever for this session;
        // treat as "no dismissal" (the durable row still wins on next load).
        if (!cancelled) setFetchedAck(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selfFetchAck]);

  const ack = bootstrapAck === undefined ? fetchedAck : bootstrapAck;
  const identity = useMemo<ReferralIdentity>(
    () => ({
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      email: user?.primaryEmailAddress?.emailAddress ?? null,
    }),
    [user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress],
  );

  const incentiveVersion = referral.incentive?.version ?? null;
  const copy = referral.isLoading
    ? null
    : referralSurfaceCopy({
        incentive: referral.incentive,
        waitlistArea,
        marketplaceLive: marketplaceEnabled,
      });
  const visible =
    !dismissedNow &&
    ack !== undefined &&
    copy !== null &&
    shouldShowReferralBanner(ack, incentiveVersion);

  const capture = useCallback<ReferralCapture>(
    (event, properties) => posthog?.capture?.(event, properties),
    [posthog],
  );

  // One impression per visible render of a given promise.
  const shownIncentive = visible ? copy.incentive : null;
  useEffect(() => {
    if (shownIncentive) captureReferralSurfaceShown(capture, "banner", shownIncentive);
  }, [capture, shownIncentive]);

  const handleDismiss = useCallback(() => {
    if (!copy) return;
    // Hide instantly for this tab, then best-effort persist (mirrors the
    // finish-profile nudge). A failed write still hides for the session.
    setDismissedNow(true);
    captureReferralSurfaceDismissed(capture, "banner", copy.incentive);
    void getToken().then((token) => {
      if (token) void putReferralBannerAck(incentiveVersion, token).catch(() => {});
    });
  }, [capture, copy, getToken, incentiveVersion]);

  if (!visible) return null;

  return (
    <ReferralBanner
      copy={copy}
      referral={referral}
      identity={identity}
      onAction={(action) =>
        captureReferralSurfaceClicked(capture, "banner", copy.incentive, action)
      }
      onDismiss={handleDismiss}
    />
  );
}
